/* ============================================================================
   Track a claim, and the merchant-scoped claim submission route.
   ----------------------------------------------------------------------------
   Both wrap real components from @enshield/public-claims. Neither component's
   contract is touched: ClaimSubmission and ClaimTracking receive the same
   props as before, and all validation, verification and submission behaviour
   is theirs. What is rebuilt is the page around them.

   The `.claim-frame` / `.claim-stack` presentation contract those components
   rely on continues to live in the legacy stylesheet and is unchanged.
   ========================================================================== */
import { useParams } from 'react-router-dom';
import { PageMeta } from '../components/PageMeta';
import { ScrollReveal } from '../components/ScrollReveal';
import { CLAIM_STEPS, ClaimSubmission, ClaimTracking } from '@enshield/public-claims';
import { Band, Btn, Card, Grid, SectionHead, Plate, Eyebrow } from '../site/primitives';
import { AstronautArt, FinalCTA, Hero } from '../site/sections';
import { ProtectionPath } from '../site/ProtectionPath';
import { Icon, type IconName } from '../site/icons';
import { ART } from '../site/media';
import { NotFoundPage } from './LegalPages';

const supportEmail = import.meta.env.VITE_SUPPORT_EMAIL || 'support@enshield.com';
const publicBaseUrl = import.meta.env.VITE_PUBLIC_BASE_URL || 'https://www.enshield.com';

const submissionDetails = [
  { detail: 'Verify your protected order.', icon: 'clipboard' as const },
  { detail: 'Choose the items affected.', icon: 'store' as const },
  { detail: 'Tell us what happened.', icon: 'search' as const },
  { detail: 'Add relevant supported files.', icon: 'file' as const },
  { detail: 'Check the information entered, then submit and receive a private token.', icon: 'lock' as const },
];
const submissionSteps = CLAIM_STEPS.filter(step => step.id !== 'done').map((step, index) => ({ label: step.label, ...submissionDetails[index] }));

/* ---- /file-a-claim/:formSlug -------------------------------------------- */
export function SubmissionRoute({ apiClient }: { apiClient: Parameters<typeof ClaimSubmission>[0]['apiClient'] }) {
  const { formSlug } = useParams();
  if (!formSlug) return <NotFoundPage />;

  return (
    <>
      <PageMeta
        title="File a claim"
        description="Submit a claim for a verified order using a merchant-specific Enshield form."
      />

      {/* The form itself is the page. Nothing competes with it above the fold. */}
      <Band space="compact" width="read" className="p-claimsurface">
        <div className="u-stack u-stack--lg">
          <div className="u-stack u-stack--sm">
            <Eyebrow>Secure claim form</Eyebrow>
            <p className="u-small p-secure">
              <Icon name="lock" aria-hidden="true" />
              Your information is transmitted securely and stays scoped to this merchant.
            </p>
          </div>
          <ClaimSubmission
            apiClient={apiClient}
            formSlug={formSlug}
            supportEmail={supportEmail}
            publicBaseUrl={publicBaseUrl}
            artworkBaseUrl=""
          />
        </div>
      </Band>

      <Band space="standard" tone="alt" labelledBy="submission-steps-title">
        <div className="u-stack u-stack--xl">
          <ScrollReveal>
            <SectionHead align="center" titleId="submission-steps-title" title="Your claim, step by step" />
          </ScrollReveal>
          <ProtectionPath stages={submissionSteps} label="Claim filing stages" />
        </div>
      </Band>

      <Band space="compact">
        <ScrollReveal className="p-callout">
          <div className="u-stack u-stack--sm">
            <h2 className="u-h3">Need help with this form?</h2>
            <p className="u-body">If something doesn’t match your order, contact support before submitting.</p>
          </div>
          <div className="u-cluster">
            <Btn tone="secondary" to="/contact">Contact claim support</Btn>
            <Btn tone="ghost" to="/faq">Read claim FAQs</Btn>
          </div>
        </ScrollReveal>
      </Band>
    </>
  );
}

/* ---- /track-claim -------------------------------------------------------- */
const helpLinks: Array<{ icon: IconName; title: string; body: string; to: string }> = [
  { icon: 'route', title: 'How claims work', body: 'Learn how we process and protect every claim.', to: '/how-it-works' },
  { icon: 'clipboard', title: 'Claims FAQ', body: 'Find answers to common questions.', to: '/faq' },
  { icon: 'clock', title: 'Response times', body: 'See our typical resolution timelines.', to: '/faq#response-times' },
  { icon: 'mail', title: 'Email support', body: 'Reach out and we’ll get back to you.', to: '/contact' },
];

export function TrackClaimPage({ apiClient }: { apiClient: Parameters<typeof ClaimTracking>[0]['apiClient'] }) {
  return (
    <div data-testid="canonical-track-claim">
      <PageMeta
        title="Track your claim"
        description="Use a private Enshield tracking token to view claim status, messages, evidence, and support."
      />

      <Hero
        compact
        titleId="track-title"
        eyebrow="Track a claim"
        title={<>Check on your claim.</>}
        lead="Enter the private tracking token from your claim confirmation email to see the current status."
        art={<AstronautArt {...ART.tracking} drift priority />}
      />

      {/* The lookup. */}
      <Band
        id="track-claim-lookup"
        space="compact"
        width="read"
        labelledBy="track-lookup-title"
        className="p-claimsurface"
      >
        <div className="u-stack u-stack--lg" data-testid="track-claim-lookup">
          <div className="u-stack u-stack--sm">
            <h2 id="track-lookup-title" className="u-h3">Enter your tracking token</h2>
            <p className="u-body">You’ll find it in the confirmation email you received when the claim was filed.</p>
          </div>
          {/*
            * The placeholder shows no example format on purpose. It used to
            * read "e.g., ENSH-1234-ABCD", which is not what a token looks like
            * — 32 characters, no dashes — and closely resembles the claim
            * REFERENCE, the identifier the confirmation email highlights and
            * which this endpoint rejects under its 20-character minimum. It
            * was training shoppers to paste the one thing guaranteed to fail.
            */}
          <ClaimTracking
            apiClient={apiClient}
            supportEmail={supportEmail}
            publicBaseUrl={publicBaseUrl}
            hideHeading
            trackingPlaceholder="Paste the tracking link or token from your email"
          />
          <p className="u-small p-secure">
            <Icon name="lock" aria-hidden="true" />
            Your information is secure. Keep your token private — it’s what grants access to this claim.
          </p>
        </div>
      </Band>

      {/* What the states mean. Someone who has just filed a claim wants to
          know what they will see next, so the vocabulary is explained here
          rather than left to be decoded from the tracker. */}
      <Band space="standard" tone="alt" labelledBy="track-states-title">
        <div className="u-stack u-stack--xl">
          <ScrollReveal>
            <SectionHead
              align="center"
              eyebrow="Claim states"
              titleId="track-states-title"
              title="What each status means"
              lead="Your claim moves through these in order. You’ll see the current one whenever you look it up."
            />
          </ScrollReveal>
          <ProtectionPath
            label="Claim status sequence"
            stages={[
              { label: 'Submitted', detail: 'We have your claim and the verified order it belongs to.', icon: 'file' },
              { label: 'In review', detail: 'Enshield is reviewing your claim with the evidence you provided.', icon: 'search' },
              { label: 'Action required', detail: 'Shown only if something is needed from you — otherwise skipped.', icon: 'bell' },
              { label: 'Resolved', detail: 'A decision has been recorded and you can see the outcome.', icon: 'check' },
            ]}
          />
        </div>
      </Band>

      <Band space="compact">
        <ScrollReveal className="p-callout">
          <div className="u-stack u-stack--sm">
            <h2 className="u-h3">Token not working?</h2>
            <p className="u-body">
              Open your claim from the link in your Enshield email — that link carries the token. A claim
              reference (ENS-XXXXXXXX) is not a token and will not work here. If the link has expired,
              contact support rather than sharing the token anywhere public.
            </p>
          </div>
          <div className="u-cluster">
            <Btn tone="secondary" to="/contact">Contact support</Btn>
          </div>
        </ScrollReveal>
      </Band>

      {/* Help resources. */}
      <Band space="standard" labelledBy="track-help-title" testId="track-claim-help">
        <ScrollReveal className="u-stack u-stack--xl">
          <SectionHead align="center" eyebrow="Support" titleId="track-help-title" title="We’re here to help" />
          <Grid cols={4}>
            {helpLinks.map((link) => (
              <Card key={link.title} to={link.to}>
                <Plate><Icon name={link.icon} /></Plate>
                <h3 className="u-h4">{link.title}</h3>
                <p className="u-small">{link.body}</p>
              </Card>
            ))}
          </Grid>
        </ScrollReveal>
      </Band>

      <FinalCTA
        titleId="track-cta-title"
        title="Built to protect. Here to support."
        body="Enshield keeps your claims connected — so you can focus on what matters."
        primary={{ label: 'File a claim', to: '/file-a-claim' }}
        secondary={{ label: 'See how it works', to: '/how-it-works' }}
      />
    </div>
  );
}
